import { Grid, IconButton, Typography } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { ValidationEvents, ValidationState } from "@store/validation-module";
import React, { useEffect } from "react";
import { useStoreon } from "storeon/react";
import { RestrictionsManager, ValidationManager } from "@modules";
import { EntityEvents, EntityState } from "@store/entity-module";
import { UniversalRestrictionChecker } from "@components/Common";
import UniversalInputField from "@components/Common/UniversalInputField";
import { AppAttribute } from "@interfaces/Internal/AppAttribute";
import { useAsync } from "@hooks";
import { vTypesService } from "@services";
import { valueTypeBindService } from "@services/Settings";
import { InputType } from "@modules/InputTypesManager";
import { toFileId } from "@modules/Utils";
import { resolveFilePickerAnchor } from "@modules/FilePickerAnchor";
import EntityAttributeItemSkeleton from "../EntityAttributeItemSkeleton";
import EntityAttributeValueSorting from "./EntityAttributeValueSorting";
import AddFilesFromStorageButton from "@components/Controls/Actions/AddFilesFromStorageButton";

interface EntityAttributeItemMultipleProps {
    attribute: AppAttribute;
}

const EntityAttributeItemMultiple = ({ attribute }: EntityAttributeItemMultipleProps) => {
    const { readOnly, attrs, dispatch } = useStoreon<EntityState, EntityEvents>(
        "readOnly",
        "attrs"
    );
    const { enableValidation, dispatch: validationDispatch } =
        useStoreon<ValidationState, ValidationEvents>("enableValidation");

    const { data: vType, loadingStatus } = useAsync(vTypesService.getValueType, {
        canLoad: !!attribute.items?.length && !!attribute.items[0]?.vt_id,
        functionArgs: [attribute.items[0].vt_id],
    });

    // нужно только чтобы решить, показывать ли "Добавить из хранилища" —
    // кнопка имеет смысл только для атрибутов типа Файл/Изображение, у
    // остальных multiple-атрибутов (текст, связи с сущностями и т.д.) её
    // быть не должно.
    const { data: vTypeBind } = useAsync(valueTypeBindService.getOneVTypeBind, {
        canLoad: !!attribute.items?.length && !!attribute.items[0]?.vt_id,
        functionArgs: [undefined, { type_id: attribute.items[0]?.vt_id }],
    });

    const isFileAttribute =
        vTypeBind?.type_name === InputType.FILE || vTypeBind?.type_name === InputType.TREE_FILE;

    const handleChangeAttributeValue = (key: string, value: any) => {
        dispatch("entity/attr/update/value", {
            at_id: attribute.atype_id,
            valueKey: key,
            value,
        });
    };

    const handleDeleteRow = (e: React.MouseEvent<HTMLButtonElement>) => {
        const key = e.currentTarget.getAttribute("data-itemkey")!;

        dispatch("entity/attr/remove/value", { aTypeId: attribute.atype_id, valueKeys: [key] });
    };

    const { valid } = ValidationManager.validateAppAttributeValueGroup(attribute.items);

    const useValidation = ValidationManager.isValidationRequired(
        attribute.required,
        attribute.items
    );

    useEffect(() => {
        validationDispatch("validation/add/validations", {
            [attribute.atype_id]: useValidation ? valid : true,
        });
    }, [validationDispatch, attribute, useValidation, valid]);

    if (loadingStatus === "loading") return <EntityAttributeItemSkeleton />;

    return (
        <>
            {isFileAttribute && !readOnly && (
                <Grid container justifyContent="flex-end">
                    <AddFilesFromStorageButton
                        attribute={attribute}
                        color="primary"
                        size="small"
                    />
                </Grid>
            )}

            {attribute.items.map((attr, itemIndex) => {
                const { valid, messages } = ValidationManager.validateEntityAttributeValue(
                    attr.value,
                    attr.restrictions
                );

                const siblingFileIds = attribute.items
                    .filter((el) => el.key !== attr.key)
                    .map((el) => toFileId(el.value))
                    .filter((v): v is number => v !== null);

                // Та же логика, что и у "Добавить из хранилища" — если у
                // этой строки уже есть своё value (file.id), FilePickerDialog
                // откроется у него, а эти пропы не используются. Нужны
                // только для ПУСТОЙ строки (в т.ч. только что созданной через
                // "Новое значение") — иначе дерево открывалось бы с вершины.
                const { anchorFileId, initialSearchTerm } = resolveFilePickerAnchor(
                    attrs,
                    siblingFileIds
                );

                return (
                    <Grid key={attr.key} container alignItems="center" spacing={2}>
                        <Grid item xs>
                            <UniversalRestrictionChecker
                                key={attr.key}
                                restrictions={attr.restrictions}
                                render={(inputTypeName, { entityRelationTypeId }) => {
                                    return (
                                        <UniversalInputField
                                            key={attr.key}
                                            vTypeId={attr.vt_id}
                                            findBy={attr.edited ? "id" : "attrValue"}
                                            responseValue="id"
                                            inputTypeName={inputTypeName}
                                            value={attr.value || ""}
                                            onChange={(name, value) => {
                                                handleChangeAttributeValue(attr.key, value);
                                            }}
                                            label={vType?.name}
                                            name={vType?.name || ""}
                                            filterByEntityTypeId={entityRelationTypeId}
                                            excludeFileIds={siblingFileIds}
                                            anchorFileId={anchorFileId}
                                            initialSearchTerm={initialSearchTerm}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            margin="normal"
                                            disabled={readOnly}
                                            error={enableValidation && useValidation && !valid}
                                            helperText={
                                                <>
                                                    {attr.restrictions.map((rest) => {
                                                        const errorMessage =
                                                            enableValidation &&
                                                            useValidation &&
                                                            messages[rest.code];

                                                        return (
                                                            <Typography
                                                                key={rest.key}
                                                                variant="caption"
                                                                display="block"
                                                                color={
                                                                    errorMessage
                                                                        ? "error"
                                                                        : "textSecondary"
                                                                }
                                                            >
                                                                {errorMessage ||
                                                                    RestrictionsManager.getRestrictionItemByCode(
                                                                        rest.code
                                                                    )?.getHelpText(rest.value)}
                                                            </Typography>
                                                        );
                                                    })}
                                                </>
                                            }
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        {!readOnly && (
                            <>
                                <EntityAttributeValueSorting
                                    atTypeId={attribute.atype_id}
                                    valueKey={attr.key}
                                    itemIndex={itemIndex}
                                    itemsLength={attribute.items.length}
                                />
                                <Grid item>
                                    <IconButton
                                        size="small"
                                        color="secondary"
                                        disabled={attribute.items.length <= 1}
                                        data-itemkey={attr.key}
                                        onClick={handleDeleteRow}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Grid>
                            </>
                        )}
                    </Grid>
                );
            })}
        </>
    );
};

export default EntityAttributeItemMultiple;
